import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Badge,
  IconButton,
  Drawer,
  Alert,
  Tooltip,
  Chip
} from '@mui/material';
import {
  WarningAmberRounded as WarningIcon,
  CloseRounded as CloseIcon,
  CheckCircleOutlineRounded as SuccessIcon,
  ArrowForwardRounded as ArrowIcon,
  AccessTimeRounded as TimeIcon
} from '@mui/icons-material';
import { getStatusText } from './utils';

const API_BASE = 'https://tbl.zerdegroup.kz/api/v1';

export interface WarningDoc {
  id: string;
  documentNumber: string;
  status: number;
  type: number;
  fromPharmacy: string;
  toPharmacy: string;
  updateDate: string;
  createdAt?: string;
}

interface WarningAlertPanelProps {
  username?: string;
}

const WarningAlertPanel: React.FC<WarningAlertPanelProps> = ({ username }) => {
  const [open, setOpen] = useState(false);
  const [warnings, setWarnings] = useState<WarningDoc[]>([]);
  const [loaded, setLoaded] = useState(false);

  const fetchWarnings = useCallback(async () => {
    const currentUsername = username || localStorage.getItem('username') || '';
    if (!currentUsername) {
      setLoaded(true);
      return;
    }

    try {
      const response = await axios.get(`${API_BASE}/documents/warnings`, {
        params: { username: currentUsername }
      });
      if (response.data.success && response.data.warnings) {
        setWarnings(response.data.warnings);
      }
    } catch (error) {
      console.error('Ошибка при загрузке предупреждений:', error);
    } finally {
      setLoaded(true);
    }
  }, [username]);

  useEffect(() => {
    fetchWarnings();
    const interval = setInterval(fetchWarnings, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchWarnings]);

  const totalCount = warnings.length;

  if (!loaded || totalCount === 0) return null;

  const getDaysStuck = (updateDate: string) => {
    const diffTime = Date.now() - new Date(updateDate).getTime();
    return Math.max(0, Math.floor(diffTime / (1000 * 60 * 60 * 24)));
  };

  const getPluralDays = (n: number) => {
    const lastDigit = n % 10;
    const lastTwoDigits = n % 100;
    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return `${n} дней`;
    if (lastDigit === 1) return `${n} день`;
    if (lastDigit >= 2 && lastDigit <= 4) return `${n} дня`;
    return `${n} дней`;
  };

  const DocListItem = ({ doc }: { doc: WarningDoc }) => {
    const daysStuck = getDaysStuck(doc.updateDate);
    const accentColor = '#ed6c02';

    return (
      <Box
        key={doc.id}
        sx={{
          mx: { xs: 2, sm: 3 },
          my: 2,
          p: 2.5,
          bgcolor: '#ffffff',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderLeft: `5px solid ${accentColor}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
            transform: 'translateY(-2px)',
            borderColor: 'divider',
            borderLeft: `5px solid ${accentColor}`
          }
        }}
      >
        {/* Header line: Doc number & Status */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, gap: 1 }}>
          <Box>
            <Typography variant="subtitle2" fontWeight="800" sx={{ color: 'text.primary', fontSize: '0.95rem' }}>
              № {doc.documentNumber}
            </Typography>
            {doc.createdAt && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                Создан: {new Date(doc.createdAt).toLocaleDateString('ru-RU')}
              </Typography>
            )}
          </Box>
          <Chip
            label={getStatusText(doc.status)}
            size="small"
            sx={{
              bgcolor: 'action.hover',
              color: 'text.secondary',
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 22
            }}
          />
        </Box>

        {/* Route visualization */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            bgcolor: 'grey.50',
            p: 1.25,
            borderRadius: 1.5,
            mb: 1.5,
            gap: 1
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 700 }}>
              Откуда
            </Typography>
            <Typography variant="body2" fontWeight="600" noWrap sx={{ color: 'text.primary' }}>
              {doc.fromPharmacy || '-'}
            </Typography>
          </Box>
          <ArrowIcon sx={{ color: 'text.disabled', fontSize: 16 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textTransform: 'uppercase', fontSize: '0.65rem', fontWeight: 700 }}>
              Куда
            </Typography>
            <Typography variant="body2" fontWeight="600" noWrap sx={{ color: 'text.primary' }}>
              {doc.toPharmacy || '-'}
            </Typography>
          </Box>
        </Box>

        {/* Footer info: Days stuck */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pt: 1.5,
            borderTop: '1px dashed',
            borderColor: 'divider',
            flexWrap: 'wrap',
            gap: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: accentColor }}>
            <TimeIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption" fontWeight="700">
              Активность: {getPluralDays(daysStuck)} назад
            </Typography>
          </Box>

          <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 600, bgcolor: 'error.50', px: 1, py: 0.25, borderRadius: 1 }}>
            Истекает срок хранения
          </Typography>
        </Box>
      </Box>
    );
  };

  const EmptyState = ({ message }: { message: string }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, px: 3, textAlign: 'center' }}>
      <Box sx={{
        width: 64,
        height: 64,
        borderRadius: '50%',
        bgcolor: 'success.50',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'success.main',
        mb: 2
      }}>
        <SuccessIcon sx={{ fontSize: 32 }} />
      </Box>
      <Typography variant="subtitle1" fontWeight="700" color="text.primary" gutterBottom>
        Все в порядке
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {message}
      </Typography>
    </Box>
  );

  return (
    <>
      {/* Кнопка для тулбара */}
      <Tooltip title="Истекающие документы на контроле">
        <IconButton
          onClick={() => setOpen(true)}
          size="small"
          color="error"
          sx={{
            border: '1px solid',
            borderColor: 'error.main',
            borderRadius: 1,
            bgcolor: 'error.50'
          }}
        >
          <Badge badgeContent={totalCount} color="warning" max={99}>
            <WarningIcon fontSize="small" />
          </Badge>
        </IconButton>
      </Tooltip>

      {/* Drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 460 }, bgcolor: '#fafafa', display: 'flex', flexDirection: 'column' }
        }}
      >
        {/* Заголовок */}
        <Box sx={{ px: 3, py: 2.5, bgcolor: '#ffffff', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Badge badgeContent={totalCount} color="error" max={999}>
              <WarningIcon color="warning" />
            </Badge>
            <Typography variant="h6" fontWeight="800" color="text.primary">
              Требуют контроля
            </Typography>
          </Box>
          <IconButton onClick={() => setOpen(false)} size="small" edge="end">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Описание */}
        <Box>
          <Alert severity="warning" sx={{ borderRadius: 0, py: 1.25 }}>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 500, lineHeight: 1.35 }}>
              Возьмите на контроль документы из списка. Если документ не будет завершен вовремя, он будет автоматически отменен, а товар вернется на остаток отправителю.
            </Typography>
          </Alert>
        </Box>

        {/* Список */}
        <Box sx={{ overflowY: 'auto', flexGrow: 1, pb: 4 }}>
          {warnings.length > 0 ? (
            <Box sx={{ mt: 1 }}>
              {warnings.map(doc => <DocListItem key={doc.id} doc={doc} />)}
            </Box>
          ) : (
            <EmptyState message="Нет зависших документов требующих контроля." />
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default WarningAlertPanel;
